import React, { useState } from "react";
import axios from "axios";
import { Send, Loader2 } from "lucide-react";
import { icbcRenewal } from "../mock";
import { useToast } from "../hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  location: "",
  comments: "",
  consentAccessICBC: false,
  consentFirstName: "",
  consentLastName: "",
  agreeNotBound: false,
};

const RenewalForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const update = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.location) {
      toast({
        title: "Missing information",
        description: "Please fill in your name, email, phone and location.",
        variant: "destructive",
      });
      return;
    }
    if (!form.consentAccessICBC || !form.agreeNotBound) {
      toast({
        title: "Consent required",
        description: "Please accept both consent statements to continue.",
        variant: "destructive",
      });
      return;
    }
    if (!form.consentFirstName || !form.consentLastName) {
      toast({
        title: "Consent name required",
        description: "Please provide your consent first and last name.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/icbc-renewals`, form);
      toast({
        title: "Renewal request submitted!",
        description:
          "Thank you. A licensed Cloverdale representative will contact you to confirm and bind your coverage.",
      });
      setForm(initial);
    } catch (err) {
      toast({
        title: "Something went wrong",
        description:
          "We couldn't submit your request. Please call us at 604-575-2222.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-[15px] text-[#0a2b52] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0066b3] focus:border-transparent transition-shadow";
  const labelCls = "block text-sm font-medium mb-1.5 text-[#0a2b52]";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Name *</label>
          <input name="name" value={form.name} onChange={update} placeholder="Full name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Phone *</label>
          <input name="phone" value={form.phone} onChange={update} placeholder="604-000-0000" className={inputCls} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Email *</label>
          <input name="email" type="email" value={form.email} onChange={update} placeholder="you@example.com" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Location *</label>
          <Select
            value={form.location}
            onValueChange={(v) => setForm((f) => ({ ...f, location: v }))}
          >
            <SelectTrigger className="w-full rounded-xl border-slate-200 bg-white/80 px-4 h-[50px] text-[15px] text-[#0a2b52] focus:ring-2 focus:ring-[#0066b3]">
              <SelectValue placeholder="Select a location" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {icbcRenewal.locations.map((loc) => (
                <SelectItem key={loc} value={loc}>
                  {loc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Additional Comments</label>
        <textarea
          name="comments"
          value={form.comments}
          onChange={update}
          rows={3}
          placeholder="Anything we should know about your renewal..."
          className={inputCls + " resize-none"}
        />
      </div>

      {/* Consent to access ICBC account */}
      <div className="rounded-2xl bg-[#0066b3]/5 border border-[#0066b3]/15 p-5">
        <div className="flex items-start gap-3">
          <Checkbox
            id="consentAccessICBC"
            checked={form.consentAccessICBC}
            onCheckedChange={(v) =>
              setForm((f) => ({ ...f, consentAccessICBC: !!v }))
            }
            className="mt-1 border-[#0066b3] data-[state=checked]:bg-[#0066b3]"
          />
          <label htmlFor="consentAccessICBC" className="text-sm font-semibold text-[#0a2b52] cursor-pointer">
            Consent To Access ICBC Account * — I accept
          </label>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed mt-3">
          {icbcRenewal.consentText}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Consent First Name *</label>
          <input name="consentFirstName" value={form.consentFirstName} onChange={update} placeholder="First name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Consent Last Name *</label>
          <input name="consentLastName" value={form.consentLastName} onChange={update} placeholder="Last name" className={inputCls} />
        </div>
      </div>

      {/* Agreement not bound */}
      <div className="flex items-start gap-3 rounded-2xl bg-white/70 border border-slate-200 p-4">
        <Checkbox
          id="agreeNotBound"
          checked={form.agreeNotBound}
          onCheckedChange={(v) =>
            setForm((f) => ({ ...f, agreeNotBound: !!v }))
          }
          className="mt-0.5 border-[#0066b3] data-[state=checked]:bg-[#0066b3]"
        />
        <label htmlFor="agreeNotBound" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
          {icbcRenewal.agreementText} <span className="text-[#0066b3] font-semibold">*</span>
        </label>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="brand-btn w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Submitting...
          </>
        ) : (
          <>
            <Send size={18} /> Submit Renewal Request
          </>
        )}
      </button>
    </form>
  );
};

export default RenewalForm;
